import { Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { lazy, Suspense, useState, useEffect, useRef } from "react";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";
import PageLoadTransition from "./components/PageLoadTransition";
import NavigationHeader from "@/components/NavigationHeader";
import { Toaster } from "@/components/ui/sonner";
import GlobalBackground from "@/components/GlobalBackground";
import IntroTextOverlay from "@/components/IntroTextOverlay";
import CookieConsentBanner from "@/components/CookieConsentBanner";

const HomePage = lazy(() => import("./pages/HomePage"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Contact = lazy(() => import("./pages/Contact"));
const Resources = lazy(() => import("./pages/Resources"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const JobHiring = lazy(() => import("./pages/JobHiring"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const DemoThankYouPage = lazy(() => import("./pages/DemoThankYouPage"));
const Login = lazy(() => import("./pages/admin/Login"));
const TransitionScreen = lazy(() => import("./pages/admin/TransitionScreen"));
const DashboardLayout = lazy(() => import("./pages/admin/DashboardLayout"));
const BlogList = lazy(() => import("./pages/admin/BlogList"));
const BlogCreate = lazy(() => import("./pages/admin/BlogCreate"));
const BlogEdit = lazy(() => import("./pages/admin/BlogEdit"));
const NewsletterSubscribers = lazy(() => import("./pages/admin/NewsletterSubscribers"));
const AccountRegistrations = lazy(() => import("./pages/admin/AccountRegistrations"));
const AccountSettings = lazy(() => import("./pages/admin/AccountSettings"));
const Partners = lazy(() => import("./pages/admin/Partners"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((module) => ({
        default: module.ReactQueryDevtools,
      }))
    )
  : null;

function RouteFallback() {
  return <div aria-hidden="true" style={{ minHeight: "30vh", width: "100%" }} />;
}

function App() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const shouldAnimate = navigationType === "PUSH";
  const [isRouteTransitionLoading, setIsRouteTransitionLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
 const [isMobileIntro] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [showDevtools, setShowDevtools] = useState(false);
  const previousPathRef = useRef(location.pathname);
  const isAdminPage =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/admin") ||
    location.pathname === "/login";
  const shouldEnableDevtools =
    import.meta.env.DEV && new URLSearchParams(location.search).get("devtools") === "1";

  // Only show the global background on public-facing pages (not admin/dashboard)
  const showGlobalBg = !isAdminPage;

  // Hide intro overlay after a short delay on mobile, longer on desktop
  useEffect(() => {
    const duration = isMobileIntro ? 500 : 1800;
    const timer = setTimeout(() => setShowIntro(false), duration);
    return () => clearTimeout(timer);
  }, [isMobileIntro]);

  useEffect(() => {
    const isPathChanged = previousPathRef.current !== location.pathname;
    previousPathRef.current = location.pathname;

    if (!isPathChanged || isAdminPage) {
      return undefined;
    }

    setIsRouteTransitionLoading(true);
    const timeoutId = setTimeout(() => {
      setIsRouteTransitionLoading(false);
    }, 2200);

    return () => clearTimeout(timeoutId);
  }, [isAdminPage, location.pathname]);

  useEffect(() => {
    setShowDevtools(shouldEnableDevtools);
  }, [shouldEnableDevtools]);

  useEffect(() => {
    const removeInjectedBacktickNodes = () => {
      const nodes = Array.from(document.body.childNodes);

      nodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;

        if (node.nodeValue?.trim() === "``") {
          node.parentNode?.removeChild(node);
        }
      });
    };

    removeInjectedBacktickNodes();

    const observer = new MutationObserver(() => {
      removeInjectedBacktickNodes();
    });

    observer.observe(document.body, { childList: true });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Global background — renders on all public pages, hidden on admin/dashboard */}
      {showGlobalBg && <GlobalBackground />}

      <IntroTextOverlay visible={showIntro} lite={isMobileIntro} />
      <PageLoadTransition isLoading={isRouteTransitionLoading} />
      <Toaster richColors position="top-right" />
      <ScrollToTop />
      <ScrollToTopButton />

      {/* Persistent header — rendered once, outside <Routes>, so it never
          unmounts/remounts on navigation. Pinned to a very high z-index so
          IntroTextOverlay/PageLoadTransition (both rendered above it) can
          never visually cover it or swallow its clicks. */}
      {!isAdminPage && (
        <div style={{ position: "relative", zIndex: 9999 }}>
          <NavigationHeader />
        </div>
      )}

      <main id="main-content">
        <AnimatePresence mode="wait">
          <Suspense fallback={<RouteFallback />}>
            <Routes location={location} key={location.pathname.split("/").slice(0, 2).join("/")}>
              <Route
                path="/"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <HomePage />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/about"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <About />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/services"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <Services />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/contact"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <Contact />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/resources"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <Resources />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/resources/:slug"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <BlogPost />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/careers"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <JobHiring />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/terms"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <Terms />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/privacy"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <Privacy />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/privacy-policy"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <Privacy />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/demo"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <DemoPage />
                  </PageLoadTransition>
                }
              />
              <Route
                path="/demo/thank-you"
                element={
                  <PageLoadTransition isFirstLoad={!shouldAnimate}>
                    <DemoThankYouPage />
                  </PageLoadTransition>
                }
              />
              <Route path="/login" element={<Login />} />
              <Route path="/admin" element={<Navigate to="/login" replace />} />
              <Route path="/admin/transition" element={<TransitionScreen />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                <Route index element={<Navigate to="newsletter" replace />} />
                <Route path="newsletter" element={<NewsletterSubscribers />} />
                <Route path="registrations" element={<AccountRegistrations />} />
                <Route path="blogs" element={<BlogList />} />
                <Route path="blogs/create" element={<BlogCreate />} />
                <Route path="blogs/edit/:id" element={<BlogEdit />} />
                <Route path="account-settings" element={<AccountSettings />} />
                <Route path="partners" element={<Partners />} />
              </Route>
              <Route path="/category/:slug" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AnimatePresence>
      </main>

      <CookieConsentBanner />

      {import.meta.env.MODE === "development" && showDevtools && ReactQueryDevtools ? (
        <Suspense fallback={null}>
          <ReactQueryDevtools
            initialIsOpen={false}
            position="bottom"
            buttonPosition="bottom-left"
            theme="dark"
          />
        </Suspense>
      ) : null}
    </>
  );
}

export default App;